/**
 * Super Admin Invoices Page - 월간 청구 워크플로우
 * 
 * 청구서 발행 / 입금 확인 / 영수증 발행을 한 화면에서 관리
 */

import { Trans, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
    FileText, Receipt, DollarSign, CheckCircle, Download,
    Loader2, ChevronLeft, ChevronRight, Eye, AlertCircle, Undo2
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { Fire } from '../../services/FireClient';
import { AdminFire } from '../../services/SuperAdminFirebase';
import { collection, getDocs, query, where, doc, updateDoc, addDoc, serverTimestamp, getDoc } from 'firebase/firestore';

interface WorkspaceBillingStatus {
    workspaceId: string;
    workspaceName: string;
    tier: string;
    amount: number;
    currency: string;
    subscriptionId?: string;
    invoiceId?: string;
    invoiceNumber?: string;
    invoiceStatus: 'none' | 'issued' | 'paid' | 'canceled';
    invoiceDate?: Date;
    paymentDate?: Date;
    receiptId?: string;
    receiptStatus: 'none' | 'issued';
    // 회사 정보 (수신자)
    companyInfo?: {
        companyName?: string;
        representative?: string;
        department?: string;
        address?: string;
        phone?: string;
        email?: string;
    };
}

interface MonthSummary {
    totalWorkspaces: number;
    invoicePending: number;
    paymentPending: number;
    receiptPending: number;
    completed: number;
    totalAmount: number;
    paidAmount: number;
}

const SuperAdminInvoicesPage: React.FC = () => {
    const { i18n } = useLingui();
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [currentMonth, setCurrentMonth] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });
    const [workspaces, setWorkspaces] = useState<WorkspaceBillingStatus[]>([]);
    const [summary, setSummary] = useState<MonthSummary>({
        totalWorkspaces: 0,
        invoicePending: 0,
        paymentPending: 0,
        receiptPending: 0,
        completed: 0,
        totalAmount: 0,
        paidAmount: 0,
    });
    const [filter, setFilter] = useState<'all' | 'invoice' | 'payment' | 'receipt' | 'completed'>('all');

    // 미리보기 다이얼로그 상태
    const [previewType, setPreviewType] = useState<'invoice' | 'receipt' | null>(null);
    const [previewData, setPreviewData] = useState<{
        ws: WorkspaceBillingStatus;
        invoiceNumber?: string;
        receiptNumber?: string;
        subtotal: number;
        taxAmount: number;
        total: number;
    } | null>(null);

    // 발행처 정보 상태
    const [issuerInfo, setIssuerInfo] = useState<{
        name: string;
        registrationNumber: string;
        address: string;
        phone: string;
        email: string;
        bankInfo: string;
    }>({
        name: 'Sunbi Soft',
        registrationNumber: 'T0000000000000',
        address: '東京都',
        phone: '',
        email: 'billing@sunbisoft.com',
        bankInfo: '',
    });

    useEffect(() => {
        loadBillingData();
    }, [currentMonth]);

    const loadBillingData = async () => {
        setLoading(true);
        try {
            // 0. 발행처 설정 로드
            const settingsDoc = await getDoc(doc(AdminFire.store, 'admin', 'settings'));
            if (settingsDoc.exists() && settingsDoc.data().issuer) {
                setIssuerInfo(prev => ({ ...prev, ...settingsDoc.data().issuer }));
            }

            // 1. 유료 워크스페이스 목록 조회
            const workspacesSnap = await getDocs(
                query(collection(Fire.store, 'workspaces'), where('tier', '!=', 'FREE'))
            );

            const billingStatuses: WorkspaceBillingStatus[] = [];
            const [year, month] = currentMonth.split('-').map(Number);
            const monthStart = new Date(year, month - 1, 1);
            const monthEnd = new Date(year, month, 0);

            for (const wsDoc of workspacesSnap.docs) {
                const wsData = wsDoc.data();

                // 구독 정보
                const subsSnap = await getDocs(
                    query(
                        collection(Fire.store, 'workspaces', wsDoc.id, 'subscriptions'),
                        where('status', '==', 'active')
                    )
                );
                const activeSub = subsSnap.docs[0]?.data();

                // 이번 달 청구서 조회
                const docsSnap = await getDocs(
                    collection(Fire.store, 'workspaces', wsDoc.id, 'billing_documents')
                );

                const thisMonthInvoice = docsSnap.docs.find(d => {
                    const data = d.data();
                    if (data.type !== 'INVOICE' || data.status === 'canceled') return false;
                    const issuedAt = data.issueDate?.toDate?.() || data.createdAt?.toDate?.();
                    return issuedAt && issuedAt >= monthStart && issuedAt <= monthEnd;
                });

                const invoiceData = thisMonthInvoice?.data();

                // 영수증 확인
                const receipt = docsSnap.docs.find(d => {
                    const data = d.data();
                    return data.type === 'RECEIPT' && data.invoiceId === thisMonthInvoice?.id;
                });

                // 회사 정보 로드
                const companyDoc = await getDoc(doc(Fire.store, 'workspaces', wsDoc.id, 'company', 'info'));
                const companyData = companyDoc.exists() ? companyDoc.data() : {};

                billingStatuses.push({
                    workspaceId: wsDoc.id,
                    workspaceName: wsData.name || wsDoc.id,
                    tier: wsData.tier || 'PREMIUM',
                    amount: activeSub?.amount || wsData.overview?.currentSubscription?.amount || 0,
                    currency: activeSub?.currency || 'JPY',
                    subscriptionId: subsSnap.docs[0]?.id,
                    invoiceId: thisMonthInvoice?.id,
                    invoiceNumber: invoiceData?.invoiceNumber,
                    invoiceStatus: invoiceData?.status === 'paid' ? 'paid' :
                        invoiceData?.status === 'issued' ? 'issued' : 'none',
                    invoiceDate: invoiceData?.issueDate?.toDate?.() || invoiceData?.createdAt?.toDate?.(),
                    paymentDate: invoiceData?.paidDate?.toDate?.(),
                    receiptId: receipt?.id,
                    receiptStatus: receipt ? 'issued' : 'none',
                    companyInfo: {
                        companyName: companyData.companyName || wsData.name,
                        representative: companyData.representative,
                        department: companyData.department,
                        address: companyData.address,
                        phone: companyData.phone,
                        email: companyData.email,
                    },
                });
            }

            setWorkspaces(billingStatuses);

            // 요약 계산
            const newSummary: MonthSummary = {
                totalWorkspaces: billingStatuses.length,
                invoicePending: billingStatuses.filter(w => w.invoiceStatus === 'none' && w.amount > 0).length,
                paymentPending: billingStatuses.filter(w => w.invoiceStatus === 'issued').length,
                receiptPending: billingStatuses.filter(w => w.invoiceStatus === 'paid' && w.receiptStatus === 'none').length,
                completed: billingStatuses.filter(w => w.invoiceStatus === 'paid' && w.receiptStatus === 'issued').length,
                totalAmount: billingStatuses.reduce((sum, w) => sum + w.amount, 0),
                paidAmount: billingStatuses.filter(w => w.invoiceStatus === 'paid').reduce((sum, w) => sum + w.amount, 0),
            };
            setSummary(newSummary);

        } catch (e) {
            console.error('Failed to load billing data:', e);
        } finally {
            setLoading(false);
        }
    };

    const changeMonth = (delta: number) => {
        const [year, month] = currentMonth.split('-').map(Number);
        const newDate = new Date(year, month - 1 + delta, 1);
        setCurrentMonth(`${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`);
    };

    const formatCurrency = (amount: number, currency: string = 'JPY') => {
        return new Intl.NumberFormat('ja-JP', { style: 'currency', currency }).format(amount);
    };

    const formatDate = (date: Date | undefined) => {
        return date ? date.toLocaleDateString('ja-JP') : '-';
    };

    // 청구서 미리보기 열기
    const openInvoicePreview = (ws: WorkspaceBillingStatus) => {
        if (!ws.amount || ws.amount <= 0) return;

        const now = new Date();
        const invoiceNumber = `INV-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

        const taxRate = 0.1;
        const total = ws.amount;
        const taxAmount = Math.floor(total - (total / (1 + taxRate)));
        const subtotal = total - taxAmount;

        setPreviewData({ ws, invoiceNumber, subtotal, taxAmount, total });
        setPreviewType('invoice');
    };

    // 청구서 실제 발행
    const handleIssueInvoice = async () => {
        if (!previewData || previewType !== 'invoice') return;
        const { ws, invoiceNumber, subtotal, taxAmount, total } = previewData;

        setActionLoading(ws.workspaceId);
        try {
            const colRef = collection(Fire.store, 'workspaces', ws.workspaceId, 'billing_documents');
            const taxRate = 0.1;

            await addDoc(colRef, {
                type: 'INVOICE',
                invoiceNumber,
                workspaceId: ws.workspaceId,
                subscriptionId: ws.subscriptionId,
                subtotal,
                taxRate,
                taxAmount,
                total,
                currency: ws.currency,
                issuer: {
                    name: issuerInfo.name,
                    registrationNumber: issuerInfo.registrationNumber,
                    address: issuerInfo.address,
                    phone: issuerInfo.phone,
                    email: issuerInfo.email,
                    bankInfo: issuerInfo.bankInfo,
                },
                recipient: {
                    companyName: ws.companyInfo?.companyName || ws.workspaceName,
                    representative: ws.companyInfo?.representative,
                    department: ws.companyInfo?.department,
                    address: ws.companyInfo?.address,
                    phone: ws.companyInfo?.phone,
                    email: ws.companyInfo?.email,
                },
                items: [{
                    description: `${ws.tier} プランサブスクリプション`,
                    quantity: 1,
                    unitPrice: subtotal,
                    amount: subtotal,
                }],
                issueDate: serverTimestamp(),
                status: 'issued',
                createdBy: 'super-admin',
                createdAt: serverTimestamp(),
            });

            setPreviewType(null);
            setPreviewData(null);
            await loadBillingData();
        } catch (e) {
            console.error('Failed to issue invoice:', e);
            alert(t(i18n)`청구서 발행에 실패했습니다.`);
        } finally {
            setActionLoading(null);
        }
    };

    // 입금 확인
    const handleConfirmPayment = async (ws: WorkspaceBillingStatus) => {
        if (!ws.invoiceId) return;

        setActionLoading(ws.workspaceId);
        try {
            const docRef = doc(Fire.store, 'workspaces', ws.workspaceId, 'billing_documents', ws.invoiceId);
            await updateDoc(docRef, {
                status: 'paid',
                paidDate: serverTimestamp(),
            });
            await loadBillingData();
        } catch (e) {
            console.error('Failed to confirm payment:', e);
            alert(t(i18n)`입금 확인에 실패했습니다.`);
        } finally {
            setActionLoading(null);
        }
    };

    // 입금 취소 (paid → issued)
    const handleCancelPayment = async (ws: WorkspaceBillingStatus) => {
        if (!ws.invoiceId) return;

        if (!window.confirm(t(i18n)`入金確認を取り消しますか？（未入金状態に戻ります）`)) return;

        setActionLoading(ws.workspaceId);
        try {
            const docRef = doc(Fire.store, 'workspaces', ws.workspaceId, 'billing_documents', ws.invoiceId);
            await updateDoc(docRef, {
                status: 'issued',
                paidDate: null,
            });
            await loadBillingData();
        } catch (e) {
            console.error('Failed to cancel payment:', e);
            alert(t(i18n)`入金取り消しに失敗しました。`);
        } finally {
            setActionLoading(null);
        }
    };

    // 청구서 취소 (issued → canceled, 다시 발행 가능)
    const handleCancelInvoice = async (ws: WorkspaceBillingStatus) => {
        if (!ws.invoiceId) return;

        if (!window.confirm(t(i18n)`請求書を取り消しますか？（取消後、再発行できます）`)) return;

        setActionLoading(ws.workspaceId);
        try {
            const docRef = doc(Fire.store, 'workspaces', ws.workspaceId, 'billing_documents', ws.invoiceId);
            await updateDoc(docRef, {
                status: 'canceled',
                canceledAt: serverTimestamp(),
                canceledBy: 'super-admin',
            });
            await loadBillingData();
        } catch (e) {
            console.error('Failed to cancel invoice:', e);
            alert(t(i18n)`請求書の取り消しに失敗しました。`);
        } finally {
            setActionLoading(null);
        }
    };

    // 영수증 미리보기 열기
    const openReceiptPreview = (ws: WorkspaceBillingStatus) => {
        if (!ws.invoiceId) return;

        const now = new Date();
        const receiptNumber = `RCP-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`;

        const taxRate = 0.1;
        const total = ws.amount;
        const taxAmount = Math.floor(total - (total / (1 + taxRate)));
        const subtotal = total - taxAmount;

        setPreviewData({ ws, receiptNumber, subtotal, taxAmount, total });
        setPreviewType('receipt');
    };

    // 영수증 실제 발행
    const handleIssueReceipt = async () => {
        if (!previewData || previewType !== 'receipt') return;
        const { ws, receiptNumber, subtotal, taxAmount, total } = previewData;
        if (!ws.invoiceId) return;

        setActionLoading(ws.workspaceId);
        try {
            const colRef = collection(Fire.store, 'workspaces', ws.workspaceId, 'billing_documents');
            const taxRate = 0.1;

            await addDoc(colRef, {
                type: 'RECEIPT',
                receiptNumber,
                invoiceId: ws.invoiceId,
                workspaceId: ws.workspaceId,
                subtotal,
                taxRate,
                taxAmount,
                total,
                currency: ws.currency,
                issuer: {
                    name: issuerInfo.name,
                    registrationNumber: issuerInfo.registrationNumber,
                    address: issuerInfo.address,
                    phone: issuerInfo.phone,
                    email: issuerInfo.email,
                    bankInfo: issuerInfo.bankInfo,
                },
                recipient: {
                    companyName: ws.companyInfo?.companyName || ws.workspaceName,
                    representative: ws.companyInfo?.representative,
                    department: ws.companyInfo?.department,
                    address: ws.companyInfo?.address,
                    phone: ws.companyInfo?.phone,
                    email: ws.companyInfo?.email,
                },
                issueDate: serverTimestamp(),
                status: 'issued',
                createdBy: 'super-admin',
                createdAt: serverTimestamp(),
            });

            setPreviewType(null);
            setPreviewData(null);
            await loadBillingData();
        } catch (e) {
            console.error('Failed to issue receipt:', e);
            alert(t(i18n)`영수증 발행에 실패했습니다.`);
        } finally {
            setActionLoading(null);
        }
    };

    // PDF 다운로드
    const handleDownloadPDF = async (ws: WorkspaceBillingStatus, docId: string) => {
        setActionLoading(ws.workspaceId);
        try {
            const generateBillingPDF = AdminFire.getFunc('generateBillingPDF');
            const result = await generateBillingPDF({ workspaceId: ws.workspaceId, documentId: docId });
            const data = result.data as { success: boolean; url?: string };
            if (data.success && data.url) {
                window.open(data.url, '_blank');
            }
        } catch (e) {
            console.error('Failed to generate PDF:', e);
        } finally {
            setActionLoading(null);
        }
    };

    // 필터링된 목록
    const filteredWorkspaces = workspaces.filter(ws => {
        if (filter === 'all') return true;
        if (filter === 'invoice') return ws.invoiceStatus === 'none' && ws.amount > 0;
        if (filter === 'payment') return ws.invoiceStatus === 'issued';
        if (filter === 'receipt') return ws.invoiceStatus === 'paid' && ws.receiptStatus === 'none';
        if (filter === 'completed') return ws.invoiceStatus === 'paid' && ws.receiptStatus === 'issued';
        return true;
    });

    const progressPercent = summary.totalWorkspaces > 0
        ? Math.round((summary.completed / summary.totalWorkspaces) * 100)
        : 0;

    const [year, month] = currentMonth.split('-').map(Number);
    const monthLabel = `${year}年${month}月`;

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 sticky top-0 z-10 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>月間請求管理</Trans>
                        </h1>
                        <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-2 py-1">
                            <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}>
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm font-medium px-2">{monthLabel}</span>
                            <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}>
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 space-y-6">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <>
                        {/* Progress Summary */}
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center justify-between mb-4">
                                    <span className="text-sm text-slate-500">全体進捗</span>
                                    <span className="text-sm font-medium">
                                        {progressPercent}% ({summary.completed}/{summary.totalWorkspaces})
                                    </span>
                                </div>
                                <div className="w-full bg-slate-200 rounded-full h-2.5">
                                    <div
                                        className="bg-indigo-600 h-2.5 rounded-full transition-all"
                                        style={{ width: `${progressPercent}%` }}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Status Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <Card
                                className={cn(
                                    "cursor-pointer transition-all",
                                    filter === 'invoice' ? 'ring-2 ring-amber-500' : 'hover:shadow-md'
                                )}
                                onClick={() => setFilter(filter === 'invoice' ? 'all' : 'invoice')}
                            >
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-amber-100 rounded-lg">
                                            <FileText className="w-5 h-5 text-amber-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.invoicePending}</div>
                                            <div className="text-xs text-slate-500">請求書発行待ち</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className={cn(
                                    "cursor-pointer transition-all",
                                    filter === 'payment' ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                                )}
                                onClick={() => setFilter(filter === 'payment' ? 'all' : 'payment')}
                            >
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <DollarSign className="w-5 h-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.paymentPending}</div>
                                            <div className="text-xs text-slate-500">入金待ち</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className={cn(
                                    "cursor-pointer transition-all",
                                    filter === 'receipt' ? 'ring-2 ring-purple-500' : 'hover:shadow-md'
                                )}
                                onClick={() => setFilter(filter === 'receipt' ? 'all' : 'receipt')}
                            >
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-purple-100 rounded-lg">
                                            <Receipt className="w-5 h-5 text-purple-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.receiptPending}</div>
                                            <div className="text-xs text-slate-500">領収書発行待ち</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card
                                className={cn(
                                    "cursor-pointer transition-all",
                                    filter === 'completed' ? 'ring-2 ring-emerald-500' : 'hover:shadow-md'
                                )}
                                onClick={() => setFilter(filter === 'completed' ? 'all' : 'completed')}
                            >
                                <CardContent className="pt-6">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-emerald-100 rounded-lg">
                                            <CheckCircle className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold">{summary.completed}</div>
                                            <div className="text-xs text-slate-500">完了</div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Workspace List */}
                        <Card>
                            <CardHeader>
                                <div className="flex items-center justify-between">
                                    <CardTitle className="text-lg">
                                        {filter === 'all' ? '全ワークスペース' :
                                            filter === 'invoice' ? '請求書発行待ち' :
                                                filter === 'payment' ? '入金待ち' :
                                                    filter === 'receipt' ? '領収書発行待ち' : '完了'}
                                        <span className="ml-2 text-sm font-normal text-slate-500">
                                            ({filteredWorkspaces.length}件)
                                        </span>
                                    </CardTitle>
                                    {filter !== 'all' && (
                                        <Button variant="ghost" size="sm" onClick={() => setFilter('all')}>
                                            全て表示
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                            <CardContent>
                                {filteredWorkspaces.length === 0 ? (
                                    <div className="text-center py-12 text-slate-400">
                                        該当するワークスペースがありません
                                    </div>
                                ) : (
                                    <div className="space-y-2">
                                        {filteredWorkspaces.map(ws => (
                                            <div
                                                key={ws.workspaceId}
                                                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div>
                                                        <div className="font-medium text-slate-900">{ws.workspaceName}</div>
                                                        <div className="text-xs text-slate-500 flex items-center gap-2">
                                                            <Badge variant="outline" className="text-xs">{ws.tier}</Badge>
                                                            <span>{formatCurrency(ws.amount, ws.currency)}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-3">
                                                    {/* Status Badge */}
                                                    <Badge className={cn(
                                                        "text-xs",
                                                        ws.invoiceStatus === 'none' ? 'bg-slate-100 text-slate-600' :
                                                            ws.invoiceStatus === 'issued' ? 'bg-amber-100 text-amber-700' :
                                                                ws.invoiceStatus === 'paid' && ws.receiptStatus === 'none' ? 'bg-blue-100 text-blue-700' :
                                                                    'bg-emerald-100 text-emerald-700'
                                                    )}>
                                                        {ws.invoiceStatus === 'none' ? '未発行' :
                                                            ws.invoiceStatus === 'issued' ? '入金待ち' :
                                                                ws.invoiceStatus === 'paid' && ws.receiptStatus === 'none' ? '領収書待ち' :
                                                                    '完了'}
                                                    </Badge>

                                                    {/* Action Buttons - 단계별 워크플로우 (가로 정렬) */}
                                                    <div className="flex items-center gap-1">
                                                        {/* Step 1: 청구서 */}
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-md">
                                                            <span className="text-[10px] text-muted-foreground font-medium">1</span>
                                                            {ws.invoiceStatus === 'none' ? (
                                                                <Button
                                                                    size="sm"
                                                                    variant="default"
                                                                    className="h-7 text-xs"
                                                                    onClick={() => openInvoicePreview(ws)}
                                                                    disabled={actionLoading === ws.workspaceId || ws.amount === 0}
                                                                >
                                                                    {actionLoading === ws.workspaceId ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                    ) : (
                                                                        <>
                                                                            <FileText className="w-3 h-3 mr-1" />
                                                                            請求書
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            ) : (
                                                                <>
                                                                    <Button
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="h-7 text-xs"
                                                                        onClick={() => ws.invoiceId && handleDownloadPDF(ws, ws.invoiceId)}
                                                                        disabled={actionLoading === ws.workspaceId}
                                                                    >
                                                                        <Download className="w-3 h-3 mr-1" />
                                                                        請求書
                                                                    </Button>
                                                                    {ws.invoiceStatus === 'issued' && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="sm"
                                                                            className="h-7 text-xs text-red-600 hover:bg-red-50 px-2"
                                                                            onClick={() => handleCancelInvoice(ws)}
                                                                            disabled={actionLoading === ws.workspaceId}
                                                                        >
                                                                            <Undo2 className="w-3 h-3" />
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>

                                                        <span className="text-muted-foreground">→</span>

                                                        {/* Step 2: 입금확인 */}
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-md">
                                                            <span className="text-[10px] text-muted-foreground font-medium">2</span>
                                                            {ws.invoiceStatus === 'issued' ? (
                                                                <Button
                                                                    size="sm"
                                                                    className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                                                    onClick={() => handleConfirmPayment(ws)}
                                                                    disabled={actionLoading === ws.workspaceId}
                                                                >
                                                                    {actionLoading === ws.workspaceId ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                    ) : (
                                                                        <>
                                                                            <CheckCircle className="w-3 h-3 mr-1" />
                                                                            入金確認
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            ) : ws.invoiceStatus === 'paid' ? (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    className="h-7 text-xs text-red-600 hover:bg-red-50"
                                                                    onClick={() => handleCancelPayment(ws)}
                                                                    disabled={actionLoading === ws.workspaceId}
                                                                >
                                                                    <Undo2 className="w-3 h-3 mr-1" />
                                                                    取消
                                                                </Button>
                                                            ) : (
                                                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled>
                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                    入金確認
                                                                </Button>
                                                            )}
                                                        </div>

                                                        <span className="text-muted-foreground">→</span>

                                                        {/* Step 3: 영수증 */}
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-md">
                                                            <span className="text-[10px] text-muted-foreground font-medium">3</span>
                                                            {ws.invoiceStatus === 'paid' && ws.receiptStatus === 'issued' ? (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    className="h-7 text-xs"
                                                                    onClick={() => ws.receiptId && handleDownloadPDF(ws, ws.receiptId)}
                                                                    disabled={actionLoading === ws.workspaceId}
                                                                >
                                                                    <Download className="w-3 h-3 mr-1" />
                                                                    領収書
                                                                </Button>
                                                            ) : ws.invoiceStatus === 'paid' && ws.receiptStatus === 'none' ? (
                                                                <Button
                                                                    size="sm"
                                                                    className="h-7 text-xs bg-purple-600 hover:bg-purple-700"
                                                                    onClick={() => openReceiptPreview(ws)}
                                                                    disabled={actionLoading === ws.workspaceId}
                                                                >
                                                                    {actionLoading === ws.workspaceId ? (
                                                                        <Loader2 className="w-3 h-3 animate-spin" />
                                                                    ) : (
                                                                        <>
                                                                            <Receipt className="w-3 h-3 mr-1" />
                                                                            領収書
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            ) : (
                                                                <Button size="sm" variant="outline" className="h-7 text-xs" disabled>
                                                                    <Receipt className="w-3 h-3 mr-1" />
                                                                    領収書
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>

            {/* 미리보기 다이얼로그 */}
            <Dialog open={previewType !== null} onOpenChange={() => { setPreviewType(null); setPreviewData(null); }}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {previewType === 'invoice' ? '請求書プレビュー' : '領収書プレビュー'}
                        </DialogTitle>
                        <DialogDescription>
                            発行内容を確認してください
                        </DialogDescription>
                    </DialogHeader>

                    {previewData && (
                        <div className="space-y-4">
                            {/* 발행처 */}
                            <div className="p-4 bg-slate-50 rounded-lg">
                                <div className="text-xs text-slate-500 mb-1">発行先</div>
                                <div className="font-medium">{previewData.ws.workspaceName}</div>
                                <Badge variant="outline" className="mt-1">{previewData.ws.tier}</Badge>
                            </div>

                            {/* 문서 번호 */}
                            <div className="p-4 bg-slate-50 rounded-lg">
                                <div className="text-xs text-slate-500 mb-1">
                                    {previewType === 'invoice' ? '請求書番号' : '領収書番号'}
                                </div>
                                <div className="font-mono text-sm">
                                    {previewType === 'invoice' ? previewData.invoiceNumber : previewData.receiptNumber}
                                </div>
                            </div>

                            {/* 금액 상세 */}
                            <div className="p-4 border rounded-lg space-y-2">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">小計</span>
                                    <span>{formatCurrency(previewData.subtotal, previewData.ws.currency)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-600">消費税 (10%)</span>
                                    <span>{formatCurrency(previewData.taxAmount, previewData.ws.currency)}</span>
                                </div>
                                <div className="border-t pt-2 flex justify-between font-bold">
                                    <span>合計</span>
                                    <span className="text-lg">{formatCurrency(previewData.total, previewData.ws.currency)}</span>
                                </div>
                            </div>

                            {/* 플랜 내역 */}
                            <div className="text-sm text-slate-600">
                                <span className="font-medium">内訳:</span> {previewData.ws.tier} プランサブスクリプション × 1
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => { setPreviewType(null); setPreviewData(null); }}>
                            キャンセル
                        </Button>
                        <Button
                            onClick={previewType === 'invoice' ? handleIssueInvoice : handleIssueReceipt}
                            disabled={actionLoading !== null}
                            className={previewType === 'invoice' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-purple-600 hover:bg-purple-700'}
                        >
                            {actionLoading !== null ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : previewType === 'invoice' ? (
                                <FileText className="w-4 h-4 mr-2" />
                            ) : (
                                <Receipt className="w-4 h-4 mr-2" />
                            )}
                            {previewType === 'invoice' ? '請求書を発行' : '領収書を発行'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default SuperAdminInvoicesPage;
